import React from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Link } from 'react-router-dom';
import { AppointmentRecurring, MatchingProfileData, getMatches, GetMatchesInput, MatchingProvidersType, AvailabilityTimeOfDayInterval, AvailabilityDayOfWeekInterval } from '@uplift/api';
import { ProviderMedicalRole } from '@uplift/types';
import { DateObject } from '@uplift/services';
import { Spinner } from '@uplift/ui-v2';
import { PROVIDERS_LIST_LENGTH } from '../../../constants';
import { LightLayout } from '../../../components';
import { useL10n } from '@uplift/hooks';
import { ProviderList, SelectedProvider } from '@uplift/pages';
import { useQuery } from '@tanstack/react-query';
import { CrisisHotlineFooter } from '../../components';
import { useInsuranceCompaniesQuery, useStateQuery } from '@uplift/queries';
import { SELF_PAY_ID } from '../../onboarding/utils';
import { MatchesUpdateFiltersData, ProvidersNextData } from '../state-machine/machine';
import { MatchingFiltersV2 } from '../../components/matches/matching-filters-v2';
import { StateMatchingFitler } from '../components/StateMatchingFilter';


const useRematchingPageStyles = makeStyles()(theme => ({
  container: {
    flex: 1,
    maxWidth: '1092px', // 1060px for content, +32px for padding on content
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(6, 0, 12),
  },
  header: {
    padding: theme.spacing(0, 2, 4),
  },
  filtersContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
  },
  filters: {
    maxWidth: '1092px',
    margin: '0 auto',
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  fullWidthMobile: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 'initial',
    },
  }
}));

export type MatchingData = {
  stateId: string | null;
  ageId: string | null;
  insuranceId: string;
  specialtyIds: string[];
  identityFilters: {
    genderIds: string[];
    faithIds: string[];
    ethnicityIds: string[];
  };
  experienceFilters: {
    communityIds: string[];
    faithIds: string[];
    ethnicityIds: string[];
  };
  availabilityTimeOfDay: AvailabilityTimeOfDayInterval[];
  availabilityDayOfWeek: AvailabilityDayOfWeekInterval[];
};

export type RematchingProvidersPageProps = {
  onBack?: () => void;
  exitAction?: React.ReactNode;
  onNext: (data: ProvidersNextData) => void;
  hasNoTherapist: boolean;
  matchProviderId?: string;

  onUpdateFilters: (data: MatchesUpdateFiltersData) => void;

  matchingData: MatchingData;
};

export const RematchingProvidersPage: React.VFC<RematchingProvidersPageProps> = props => {
  const l10n = useL10n();
  const { classes } = useRematchingPageStyles();
  const [ selectedProvider, setSelectedProvider ] = React.useState<MatchingProfileData>();
  const [ providersToShow, setProvidersToShow ] = React.useState(PROVIDERS_LIST_LENGTH);
  const { data: state } = useStateQuery(props.matchingData.stateId);
  const { data: companies = [], isLoading: insuranceCompaniesLoading } = useInsuranceCompaniesQuery({ stateId: props.matchingData.stateId });

  const matchingQueryInput: GetMatchesInput = {
    seed: 0.3,
    stateId: props.matchingData.stateId,
    medicalRole: ProviderMedicalRole.therapist,
    ageGroupId: props.matchingData.ageId,
    insuranceCompanyId: props.matchingData.insuranceId !== SELF_PAY_ID ? props.matchingData.insuranceId : null,
    visitReasonIds: props.matchingData.specialtyIds,
    genderIds: props.matchingData.identityFilters.genderIds,
    faithIds: props.matchingData.identityFilters.faithIds,
    ethnicityIds: props.matchingData.identityFilters.ethnicityIds,
    communityExpertise: {
      faithIds: props.matchingData.experienceFilters.faithIds,
      ethnicityIds: props.matchingData.experienceFilters.ethnicityIds,
      generalIds: props.matchingData.experienceFilters.communityIds,
    },
    availableWeekdays: props.matchingData.availabilityDayOfWeek,
    availableDayIntervals: props.matchingData.availabilityTimeOfDay,

    rematch: true,
  };

  const { data, isLoading: matchesLoading } = useQuery({
    queryKey: [ 'get-matches', matchingQueryInput ],
    queryFn: () => getMatches(matchingQueryInput),
    select: data => data.data,
  });
  const matchingProfiles = data?.matchingProfiles?.filter(({ user }) => user.id !== props.matchProviderId) ?? [];
  const matchingPaymentType = data?.type;

  const matchedInsuranceCompany = matchingPaymentType === MatchingProvidersType.cashpay
    ? null
    : companies.find(({ id }) => id === props.matchingData.insuranceId) ?? null;

  const isLoading = matchesLoading || insuranceCompaniesLoading;

  const onScheduleSessionHandler = (selectedSlot: DateObject) => {
    props.onNext({
      selectedSlot: {
        fromDatetime: selectedSlot,
        recurring: AppointmentRecurring.weekly,
      },
      newProvider: {
        id: selectedProvider.user.id,
        first_name: selectedProvider.user.firstName,
        last_name: selectedProvider.user.lastName,
        avatar_url: selectedProvider.avatarAsset.url,
        preferred_name: selectedProvider.profile.preferredName,
      }
    });
  };

  const filters = (
    <div className={classes.filtersContainer}>
      <div className={classes.filters}>
        <StateMatchingFitler
          stateShortName={state?.shortName || ''}
        />
        <MatchingFiltersV2
          className={classes.fullWidthMobile}
          disableInsuranceSelect
          onUpdateFilters={props.onUpdateFilters}
          matchingData={{
            ...props.matchingData,
            medicalRole: ProviderMedicalRole.therapist,
          }}
        />
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <LightLayout leftActions={props.exitAction}>
        {filters}
        <Box my={20}>
          <Spinner />
        </Box>
      </LightLayout>
    );
  }

  if (selectedProvider) {
    return (
      <LightLayout onBack={() => setSelectedProvider(undefined)} leftActions={props.exitAction}>
        <SelectedProvider
          stateId={state?.id}
          matchedInsuranceCompany={matchedInsuranceCompany}
          onScheduleSession={onScheduleSessionHandler}
          excludeProviderIds={props.matchProviderId ? [ props.matchProviderId ] : undefined}
          selectedProvider={selectedProvider.user.id}
          isScrollableView
          selectedSpecialtyIds={props.matchingData.specialtyIds}
        />
      </LightLayout>
    );
  }

  const hasProviders = (matchingProfiles.length > 0);

  return (
    <LightLayout leftActions={props.exitAction}>
      {filters}
      <Box className={classes.container} marginX="auto">
        {hasProviders && (
          <Box className={classes.header}>
            <Typography variant="h1">
              {l10n.rematching.providers.found(matchingProfiles.length)}{' '}
              <Typography variant="h1" component="span" color="secondary">
                {l10n.rematching.providers.forYou}
              </Typography>
            </Typography>
            {!props.hasNoTherapist && (
              <Box mt={1}>
                <Typography variant="body1" color="textSecondary">
                  {l10n.rematching.providers.noChange}{' '}
                  <Typography component={Link} to="/" color="secondary">
                    {l10n.rematching.providers.return}
                  </Typography>
                </Typography>
              </Box>
            )}
          </Box>
        )}
        <Box maxWidth="792px" px={{ sm: 2 }}>
          <ProviderList
            stateId={props.matchingData.stateId}
            matchingProfiles={matchingProfiles}
            insuranceCompanyId={props.matchingData.insuranceId}
            onViewClick={provider => setSelectedProvider(provider)}
            providersToShow={providersToShow}
            onLoadMore={() => setProvidersToShow(matchingProfiles.length)}
            matchingPaymentType={data?.type}
            noInsuranceProviders={data?.noInsuranceProviders}
            emptyListRelaxed={data?.emptyListRelaxed}
          />
        </Box>
      </Box>
      <CrisisHotlineFooter />
    </LightLayout>
  );
};
